import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './stores/authStore';
import { Layout } from './components/layout/Layout';
import { ToastContainer } from './components/ui/Toast';
import { LoginPage } from './pages/Login';
import { DashboardPage } from './pages/Dashboard';
import { ServersPage } from './pages/servers/index';
import { NewServerPage } from './pages/servers/new';
import { ServerDetailPage } from './pages/servers/detail';
import { EditServerPage } from './pages/servers/edit';
import { AppsPage } from './pages/apps/index';
import { NewAppPage } from './pages/apps/new';
import { AppDetailPage } from './pages/apps/detail';
import { EditAppPage } from './pages/apps/edit';
import { DomainsPage } from './pages/domains/index';
import { NewDomainPage } from './pages/domains/new';
import { DomainDetailPage } from './pages/domains/detail';
import { UsersPage } from './pages/users/index';
import { NewUserPage } from './pages/users/new';
import { TerminalPage } from './pages/terminal/index';
import { MonitoringPage } from './pages/monitoring/index';

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore();

  // Show loading state while checking auth
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function App() {
  const { fetchUser } = useAuthStore();

  // Validate token on app startup
  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  return (
    <BrowserRouter>
      <ToastContainer />
      <Routes>
        {/* Public routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />

        {/* Private routes */}
        <Route
          path="/"
          element={
            <PrivateRoute>
              <Layout />
            </PrivateRoute>
          }
        >
          <Route index element={<DashboardPage />} />

          {/* Servers */}
          <Route path="servers" element={<ServersPage />} />
          <Route path="servers/new" element={<NewServerPage />} />
          <Route path="servers/:id" element={<ServerDetailPage />} />
          <Route path="servers/:id/edit" element={<EditServerPage />} />

          {/* Apps */}
          <Route path="apps" element={<AppsPage />} />
          <Route path="apps/new" element={<NewAppPage />} />
          <Route path="apps/:id" element={<AppDetailPage />} />
          <Route path="apps/:id/edit" element={<EditAppPage />} />

          {/* Domains */}
          <Route path="domains" element={<DomainsPage />} />
          <Route path="domains/new" element={<NewDomainPage />} />
          <Route path="domains/:id" element={<DomainDetailPage />} />

          {/* Users */}
          <Route path="users" element={<UsersPage />} />
          <Route path="users/new" element={<NewUserPage />} />

          {/* Monitoring */}
          <Route path="monitoring" element={<MonitoringPage />} />

          {/* Terminal */}
          <Route path="terminal" element={<TerminalPage />} />
        </Route>

        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
